import { useState, useCallback, useEffect } from 'react'
import { NavLink, useNavigate } from 'react-router-dom'
import { CalendarRange, NotebookPen, Hash, Settings2, Plus, X, LogOut } from 'lucide-react'
import pkg from '../../../package.json'
import { useSections, useCreateSection, useDeleteSection } from '../../api/hooks'
import { useToast } from '../primitives/Toast'
import { useAuthStore } from '../../store/auth'
import { logoutAPI } from '../../api/auth'

const TAGLINE = 'deliberate thought'

interface MobileNavDrawerProps {
  open: boolean
  onClose(): void
}

export function MobileNavDrawer({ open, onClose }: MobileNavDrawerProps) {
  const { data: sections = [] } = useSections()
  const createSection = useCreateSection()
  const deleteSection = useDeleteSection()
  const { showToast } = useToast()
  const navigate = useNavigate()

  const [newSectionName, setNewSectionName] = useState('')
  const [addingSection, setAddingSection] = useState(false)

  // Lock body scroll while open.
  useEffect(() => {
    if (open) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = ''
    }
    return () => { document.body.style.overflow = '' }
  }, [open])

  const close = useCallback(() => {
    setAddingSection(false)
    setNewSectionName('')
    onClose()
  }, [onClose])

  const handleNavigate = useCallback((to: string) => {
    navigate(to)
    close()
  }, [navigate, close])

  const handleSignOut = useCallback(() => {
    logoutAPI()
    useAuthStore.getState().clear()
    navigate('/login')
    close()
  }, [navigate, close])

  const handleCreateSection = useCallback(() => {
    const name = newSectionName.trim()
    if (!name) return
    const maxPos = sections.length > 0 ? Math.max(...sections.map((s) => s.position)) : 0
    createSection.mutate(
      { name, position: maxPos + 1 },
      {
        onSuccess: () => {
          setNewSectionName('')
          setAddingSection(false)
          showToast(`Section "${name}" created`, 'success')
        },
        onError: () => showToast('Failed to create section', 'error'),
      }
    )
  }, [newSectionName, sections, createSection, showToast])

  return (
    <>
      {/* Backdrop */}
      <div
        onClick={close}
        aria-hidden={!open}
        style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(0,0,0,0.4)',
          zIndex: 200,
          opacity: open ? 1 : 0,
          pointerEvents: open ? 'auto' : 'none',
          transition: 'opacity 200ms ease',
        }}
      />

      {/* Drawer panel */}
      <div
        aria-hidden={!open}
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          bottom: 0,
          width: '80vw',
          maxWidth: '300px',
          background: 'var(--color-paper)',
          zIndex: 201,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          boxShadow: 'var(--shadow-md)',
          transform: open ? 'translateX(0)' : 'translateX(-100%)',
          transition: 'transform 240ms cubic-bezier(0.22, 0.61, 0.36, 1)',
          willChange: 'transform',
        }}
      >
        {/* App header */}
        <div style={{ padding: '20px 18px 16px', borderBottom: '1px solid var(--color-border)' }}>
          <div
            style={{
              fontFamily: 'var(--font-serif)',
              fontSize: '1.375rem',
              fontWeight: 600,
              color: 'var(--color-ink)',
              letterSpacing: '-0.02em',
            }}
          >
            todux
          </div>
          <div
            style={{
              display: 'flex',
              alignItems: 'baseline',
              justifyContent: 'space-between',
              gap: '12px',
              marginTop: '2px',
            }}
          >
            <div
              style={{
                fontFamily: 'var(--font-serif)',
                fontStyle: 'italic',
                fontSize: '0.75rem',
                color: 'var(--color-ink-faint)',
              }}
            >
              {TAGLINE}
            </div>
            <div
              style={{
                fontFamily: 'var(--font-mono)',
                fontSize: '0.6875rem',
                color: 'var(--color-ink-faint)',
              }}
            >
              v{pkg.version}
            </div>
          </div>
        </div>

        {/* Nav */}
        <nav style={{ flex: 1, overflowY: 'auto', padding: '8px 0' }}>
          <DrawerNavItem to="/week" label="Week" icon={<CalendarRange size={16} strokeWidth={1.5} />} onClick={() => handleNavigate('/week')} />

          {sections.length > 0 && (
            <div style={{ marginTop: '12px' }}>
              <div className="eyebrow" style={{ padding: '8px 18px' }}>Sections</div>
              {[...sections]
                .sort((a, b) => a.position - b.position)
                .map((section) => (
                  <div key={section.id} style={{ display: 'flex', alignItems: 'center' }}>
                    <DrawerNavItem
                      to={`/sections/${section.id}`}
                      label={section.name}
                      icon={<Hash size={16} strokeWidth={1.5} />}
                      onClick={() => handleNavigate(`/sections/${section.id}`)}
                      style={{ flex: 1 }}
                    />
                    <button
                      onClick={() => {
                        if (window.confirm(`Delete section "${section.name}"?`)) {
                          deleteSection.mutate(section.id, {
                            onError: () => showToast('Failed to delete section', 'error'),
                          })
                        }
                      }}
                      style={{
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        color: 'var(--color-ink-faint)',
                        padding: '8px',
                        display: 'flex',
                        alignItems: 'center',
                      }}
                      aria-label={`Delete ${section.name}`}
                    >
                      <X size={14} strokeWidth={1.5} />
                    </button>
                  </div>
                ))}
            </div>
          )}

          <div style={{ height: '12px' }} />
          <DrawerNavItem to="/notes" label="Notes" icon={<NotebookPen size={16} strokeWidth={1.5} />} onClick={() => handleNavigate('/notes')} />
        </nav>

        {/* Bottom actions */}
        <div style={{ borderTop: '1px solid var(--color-border)', padding: '8px 0 12px' }}>
          {addingSection ? (
            <div style={{ display: 'flex', gap: '4px', padding: '8px 18px' }}>
              <input
                value={newSectionName}
                onChange={(e) => setNewSectionName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleCreateSection()
                  if (e.key === 'Escape') { setAddingSection(false); setNewSectionName('') }
                }}
                placeholder="Section name..."
                autoFocus
                style={{
                  flex: 1,
                  background: 'transparent',
                  border: 'none',
                  borderBottom: '1px solid var(--color-accent)',
                  padding: '4px 0',
                  fontFamily: 'var(--font-mono)',
                  fontSize: '0.875rem',
                  color: 'var(--color-ink)',
                  outline: 'none',
                }}
              />
              <button
                onClick={handleCreateSection}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  color: 'var(--color-accent)',
                  padding: '2px',
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                <Plus size={14} strokeWidth={1.5} />
              </button>
            </div>
          ) : (
            <DrawerActionRow label="New Section" icon={<Plus size={16} strokeWidth={1.5} />} onClick={() => setAddingSection(true)} />
          )}
          <DrawerNavItem to="/settings" label="Settings" icon={<Settings2 size={16} strokeWidth={1.5} />} onClick={() => handleNavigate('/settings')} />
          <DrawerActionRow label="Sign out" icon={<LogOut size={16} strokeWidth={1.5} />} onClick={handleSignOut} />
        </div>
      </div>
    </>
  )
}

interface DrawerNavItemProps {
  to: string
  label: string
  icon: React.ReactNode
  onClick(): void
  style?: React.CSSProperties
}

function DrawerNavItem({ to, label, icon, onClick, style: extraStyle }: DrawerNavItemProps) {
  return (
    <NavLink
      to={to}
      onClick={onClick}
      style={({ isActive }) => ({
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        padding: '10px 18px',
        fontFamily: 'var(--font-serif)',
        fontSize: '1rem',
        color: isActive ? 'var(--color-ink)' : 'var(--color-ink-muted)',
        textDecoration: 'none',
        background: isActive ? 'var(--color-paper-dark)' : 'transparent',
        fontWeight: isActive ? 500 : 400,
        ...extraStyle,
      })}
    >
      <span style={{ display: 'inline-flex', alignItems: 'center', flexShrink: 0, color: 'var(--color-ink-faint)' }}>
        {icon}
      </span>
      {label}
    </NavLink>
  )
}

interface DrawerActionRowProps {
  label: string
  icon: React.ReactNode
  onClick(): void
}

function DrawerActionRow({ label, icon, onClick }: DrawerActionRowProps) {
  return (
    <button
      onClick={onClick}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        width: '100%',
        padding: '10px 18px',
        fontFamily: 'var(--font-serif)',
        fontSize: '1rem',
        color: 'var(--color-ink-muted)',
        background: 'none',
        border: 'none',
        cursor: 'pointer',
        textAlign: 'left',
      }}
    >
      <span style={{ display: 'inline-flex', alignItems: 'center', flexShrink: 0, color: 'var(--color-ink-faint)' }}>
        {icon}
      </span>
      {label}
    </button>
  )
}
